import { RestaurantDetails } from '../types';

/**
 * Checks if restaurant details are complete and valid
 * @param restaurantDetails - The restaurant details object to check
 * @returns boolean - true if details are complete, false otherwise
 */
export const isRestaurantDetailsComplete = (restaurantDetails: RestaurantDetails | null): boolean => {
  if (!restaurantDetails) {
    return false;
  }

  // Check if essential fields are present and not empty
  const requiredFields = ['name', 'id', 'address', 'description'];
  
  for (const field of requiredFields) {
    const value = restaurantDetails[field as keyof RestaurantDetails];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return false;
    }
  }

  return true;
};

/**
 * Gets restaurant details from localStorage
 * @returns RestaurantDetails | null
 */
export const getRestaurantDetailsFromStorage = (): RestaurantDetails | null => {
  try {
    const stored = localStorage.getItem('restaurantDetails');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error parsing restaurant details from localStorage:', error);
  }
  return null;
};

/**
 * Checks if restaurant details are missing from localStorage
 * @returns boolean - true if details are missing or incomplete
 */
export const isRestaurantDetailsMissing = (): boolean => {
  const details = getRestaurantDetailsFromStorage();
  return !isRestaurantDetailsComplete(details);
};

const ensureProtocol = (value: string): string => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const isLikelyFullDomain = (value: string | undefined | null): boolean => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return /^https?:\/\//i.test(trimmed) || trimmed.includes('.');
};

const sanitizeSubdomain = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const isLocalLikeHost = (hostname: string): boolean => {
  if (!hostname) return false;
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
  );
};

const resolveBaseUrl = (rawBase: string | undefined): URL => {
  const fallback = 'https://menunix.com';
  const candidate = rawBase && rawBase.trim() ? rawBase.trim() : fallback;

  try {
    return new URL(candidate);
  } catch {
    try {
      return new URL(ensureProtocol(candidate));
    } catch {
      return new URL(fallback);
    }
  }
};

export type PublicMenuOrderType = 'delivery' | 'pickup' | 'swiggy' | 'zomato';

export type BuildRestaurantFrontendOptions = {
  tableName?: string;
  /** Appended as orderType=… for delivery, pickup, and aggregator flows (not used with table URLs). */
  orderType?: PublicMenuOrderType;
};

const appendPublicMenuQuery = (href: string, options?: BuildRestaurantFrontendOptions): string => {
  const trimmed = href.replace(/\/+$/, '');
  const parts: string[] = [];
  if (options?.tableName) {
    parts.push(`t=${encodeURIComponent(options.tableName)}`);
  }
  if (options?.orderType) {
    parts.push(`orderType=${encodeURIComponent(options.orderType)}`);
  }
  if (parts.length === 0) {
    return trimmed;
  }
  const sep = trimmed.includes('?') ? '&' : '?';
  return `${trimmed}${sep}${parts.join('&')}`;
};

export const buildRestaurantFrontendOrigin = (
  restaurantDetails?: Partial<RestaurantDetails> & Record<string, any>,
  options?: BuildRestaurantFrontendOptions
): string => {
  const baseEnv = (import.meta as any)?.env?.VITE_API_FROMTEND_URL as string | undefined;
  const baseUrl = resolveBaseUrl(baseEnv);

  const rawId =
    (restaurantDetails?.id as string | undefined) ||
    (restaurantDetails?._id as string | undefined) ||
    '';

  if (isLikelyFullDomain(rawId)) {
    const full = ensureProtocol(rawId.trim()).replace(/\/+$/, '');
    return appendPublicMenuQuery(full, options);
  }

  const subdomain = sanitizeSubdomain(rawId || 'your-shop');

  if (!subdomain) {
    const base = baseUrl.href.replace(/\/+$/, '');
    return appendPublicMenuQuery(base, options);
  }

  if (isLocalLikeHost(baseUrl.hostname)) {
    const origin = baseUrl.origin.replace(/\/+$/, '');
    const path = `/${subdomain}`;
    return appendPublicMenuQuery(`${origin}${path}`, options);
  }

  baseUrl.hostname = `${subdomain}.${baseUrl.hostname}`;
  baseUrl.pathname = '';
  baseUrl.search = '';
  baseUrl.hash = '';
  const origin = baseUrl.origin.replace(/\/+$/, '');
  return appendPublicMenuQuery(origin, options);
};

export const buildRestaurantTableUrl = (
  restaurantDetails: Partial<RestaurantDetails> & Record<string, any> | undefined,
  tableName: string
): string => {
  return buildRestaurantFrontendOrigin(restaurantDetails, { tableName });
};

/** Canonical shop id from JWT (`auth_token`) for APIs that expect `shopId` (e.g. product table timestamp). */
export const getShopIdFromAuthToken = (): string => {
  try {
    const authToken = localStorage.getItem('auth_token') || '';
    if (!authToken) return '';
    const payload = authToken.split('.')[1] || '';
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return String(parsed?.data?.shopId ?? parsed?.data?.shopusername ?? '').trim();
  } catch {
    return '';
  }
};

/** Public-menu subdomain / shop username from JWT when present (not the same as `shopId` for backend APIs). */
export const getShopUsernameFromAuthToken = (): string => {
  try {
    const authToken = localStorage.getItem('auth_token') || '';
    if (!authToken) return '';
    const payload = authToken.split('.')[1] || '';
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return String(parsed?.data?.shopusername ?? '').trim();
  } catch {
    return '';
  }
};